import {
  DisplayReturn,
  ReturnProduct,
} from "@redotech/customer-portal-sdk/rpc/schema/get-returns-by-order-id";
import { useLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Order } from "@redotech/redo-model/order";
import {
  CompensationMethod,
  ReturnTypeEnum,
} from "@redotech/redo-model/return";
import {
  ReturnStatus,
  isReturnStatusDisplayedRejected,
} from "@redotech/redo-model/return-status";
import { LineItem } from "@redotech/redo-model/shopify-order";
import { useContext } from "react";

// Define ReturnedLineItem interface
export interface ReturnedLineItem extends LineItem {
  _returnId?: string;
  _returnedItem?: boolean;
}

/**
 * Hook to fetch returns for a specific order
 * @param order The order to fetch returns for
 * @returns Loading state and returns data
 */
export function useOrderReturns(order: Order) {
  const orderId = order._id;
  const rpcClient = useContext(RpcClientContext);

  const returnsLoad = useLoad(async () => {
    if (!rpcClient || !orderId) {
      return { returns: [], hasReturnableItems: false };
    }

    try {
      // Call the RPC method to get returns by order ID
      const draftReturn = await rpcClient.createDraftReturn({
        returnType: ReturnTypeEnum.RETURN,
        orderId,
      });
      const hasReturnableItems =
        draftReturn.draftReturn.returnableItems.length > 0;
      const result = await rpcClient.getReturnsByOrderId({ orderId });
      return { ...result, hasReturnableItems };
    } catch (error) {
      console.error("Error fetching returns for order", orderId, error);
      // Return empty array for returns to avoid breaking the UI
      return { returns: [], hasReturnableItems: false };
    }
  }, [rpcClient, orderId]);

  return {
    isLoading: returnsLoad.pending,
    returns: returnsLoad.value?.returns || [],
    hasReturnableItems: returnsLoad.value?.hasReturnableItems || false,
    hasError: !!returnsLoad.error,
    error: returnsLoad.error,

    // Helper functions that work with the returns data
    getReturnedQuantity: (lineItemId: string) => {
      const returns = returnsLoad.value?.returns || [];
      return getTotalReturnedQuantity(lineItemId, returns);
    },

    getReturnableQuantity: (lineItem: LineItem) => {
      const returns = returnsLoad.value?.returns || [];
      return getReturnableQuantity(lineItem, returns);
    },

    hasReturnableQuantity: (lineItem: LineItem) => {
      const returns = returnsLoad.value?.returns || [];
      return hasReturnableQuantity(lineItem, returns);
    },

    getReturnsByReturnId: () => {
      const returns = returnsLoad.value?.returns || [];
      return groupReturnsByReturnId(returns);
    },

    getReturnInfo: (returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      return returns.find((r) => r._id === returnId);
    },

    getReturnedQuantityForReturn: (lineItemId: string, returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      return getReturnedQuantityForReturn(lineItemId, returnId, returns);
    },

    separateLineItems: (lineItems: LineItem[]) => {
      const returns = returnsLoad.value?.returns || [];
      return separateLineItems(lineItems, returns);
    },

    getReturnedItemsByReturnId: (lineItems: LineItem[]) => {
      const returns = returnsLoad.value?.returns || [];
      const { returnedItemsByReturnId } = separateLineItems(lineItems, returns);
      return returnedItemsByReturnId;
    },

    getReturnCompletionMessage: (returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      const returnData = returns.find((r) => r._id === returnId);
      return getReturnCompletionMessage(returnData);
    },

    getReturnHeaderText: (returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      const returnData = returns.find((r) => r._id === returnId);
      return getReturnHeaderText(returnData);
    },
  };
}

/**
 * Calculate total returned quantity for a line item across all returns
 */
function getTotalReturnedQuantity(
  lineItemId: string,
  returns: DisplayReturn[],
): number {
  if (!returns || returns.length === 0) {
    return 0;
  }

  let totalReturned = 0;

  // Loop through all returns
  returns.forEach((returnData) => {
    // Find all products in this return that match the line item ID
    const matchingProducts =
      returnData.products?.filter(
        (p: ReturnProduct) =>
          p.line_item_id?.toString() === lineItemId.toString(),
      ) || [];

    // Add up the quantities from all matching products
    matchingProducts.forEach((product: ReturnProduct) => {
      totalReturned += product.quantity || 0;
    });
  });

  return totalReturned;
}

/**
 * Get the returnable quantity for a line item
 */
function getReturnableQuantity(
  lineItem: LineItem,
  returns: DisplayReturn[],
): number {
  const returnedQuantity = getTotalReturnedQuantity(
    lineItem.id.toString(),
    returns,
  );
  return Math.max(0, lineItem.quantity - returnedQuantity);
}

/**
 * Check if a line item has any returnable quantity
 */
function hasReturnableQuantity(
  lineItem: LineItem,
  returns: DisplayReturn[],
): boolean {
  return getReturnableQuantity(lineItem, returns) > 0;
}

/**
 * Group returns by return ID
 */
function groupReturnsByReturnId(
  returns: DisplayReturn[],
): Map<string, DisplayReturn> {
  if (!returns || returns.length === 0) {
    return new Map();
  }

  // Create a map of returnId -> return object
  const returnMap = new Map<string, DisplayReturn>();
  returns.forEach((returnData) => {
    returnMap.set(returnData._id, returnData);
  });

  return returnMap;
}

/**
 * Get the quantity returned for a specific line item in a specific return
 */
function getReturnedQuantityForReturn(
  lineItemId: string,
  returnId: string,
  returns: DisplayReturn[],
): number {
  if (!returns || returns.length === 0) {
    return 0;
  }

  const returnData = returns.find((r) => r._id === returnId);
  if (!returnData) {
    return 0;
  }

  const product = returnData.products?.find(
    (p: ReturnProduct) => p.line_item_id?.toString() === lineItemId.toString(),
  );

  return product?.quantity || 0;
}

/**
 * Separate line items into returnable and non-returnable items
 * Group returned items by return ID
 */
function separateLineItems(
  lineItems: LineItem[],
  returns: DisplayReturn[],
): {
  returnableItems: LineItem[];
  returnedItems: ReturnedLineItem[];
  returnedItemsByReturnId: Map<string, ReturnedLineItem[]>;
} {
  const returnableItems: LineItem[] = [];
  const returnedItems: ReturnedLineItem[] = [];
  const returnedItemsByReturnId = new Map<string, ReturnedLineItem[]>();

  lineItems.forEach((lineItem) => {
    // Get the returnable quantity and total returned quantity for this line item
    const returnableQuantity = getReturnableQuantity(lineItem, returns);
    const returnedQuantity = getTotalReturnedQuantity(
      lineItem.id.toString(),
      returns,
    );

    // If the item has any returnable quantity, add it to returnableItems
    if (returnableQuantity > 0) {
      returnableItems.push(lineItem);
    }

    // If the item has any returned quantity, find all returns with this item
    if (returnedQuantity > 0) {
      // Look through all returns to find ones containing this line item
      returns.forEach((returnData) => {
        const matchingProducts =
          returnData.products?.filter(
            (p: ReturnProduct) =>
              p.line_item_id?.toString() === lineItem.id.toString(),
          ) || [];

        // For each matching product, create a returned line item
        matchingProducts.forEach((product: ReturnProduct) => {
          // Clone the line item and update its quantity to show just the returned amount for this specific return
          const returnedLineItem: ReturnedLineItem = {
            ...lineItem,
            // Override the quantity to be the returned quantity instead of the original
            quantity: product.quantity || 0,
            // Add a flag to indicate this is a "returned" version of the item
            _returnedItem: true,
            // Add the return ID to link this item to its parent return
            _returnId: returnData._id,
          };

          // Only add it if there's a positive quantity
          if (returnedLineItem.quantity > 0) {
            returnedItems.push(returnedLineItem);

            // Also add to the returnedItemsByReturnId map
            if (!returnedItemsByReturnId.has(returnData._id)) {
              returnedItemsByReturnId.set(returnData._id, []);
            }
            returnedItemsByReturnId.get(returnData._id)?.push(returnedLineItem);
          }
        });
      });
    }

    // If the item has never been returned and has no returnable quantity
    // (this should only occur if it's unfulfilled)
    if (returnableQuantity === 0 && returnedQuantity === 0) {
      returnableItems.push(lineItem);
    }
  });

  return { returnableItems, returnedItems, returnedItemsByReturnId };
}

/**
 * Get the appropriate return/exchange completion message
 */
function getReturnCompletionMessage(
  returnData: DisplayReturn | undefined,
): string {
  // Check return status first
  if (!returnData) return "Your return was rejected.";

  const returnStatus = returnData.status;

  const isRejected =
    isReturnStatusDisplayedRejected[returnStatus as ReturnStatus];

  // Check if the return is rejected
  if (isRejected) {
    return "Your return was rejected.";
  }

  // Check if the return is open
  if (returnStatus === ReturnStatus.OPEN) {
    return "Your return is waiting to be processed.";
  }

  // Check return strategy for completed returns
  if (returnStatus === ReturnStatus.COMPLETE) {
    if (returnData.type === ReturnTypeEnum.CLAIM) {
      return "Your claim is complete.";
    } else if (returnData.type === ReturnTypeEnum.WARRANTY) {
      return "Your warranty is complete.";
    } else if (returnData.type === ReturnTypeEnum.MANAGED_CLAIM) {
      return "Your managed claim is complete.";
    }
    if (returnData.returnType === CompensationMethod.STORE_CREDIT) {
      return "Your return is complete and store credit has been issued.";
    } else if (returnData.returnType === CompensationMethod.EXCHANGE) {
      return "Your exchange is complete.";
    } else {
      return "Your return is complete and a refund has been issued.";
    }
  }

  // Default message for any other case
  return "Your return is being processed.";
}

/**
 * Get the appropriate return/exchange header text
 */
function getReturnHeaderText(returnData: DisplayReturn | undefined): string {
  // Handle no return data
  if (!returnData) {
    return "Return rejected";
  }

  const returnStatus = returnData.status;
  const isRejected =
    (ReturnStatus.REJECTED || ReturnStatus.DELETED) === returnStatus;
  // For completed returns
  if (returnStatus === ReturnStatus.COMPLETE) {
    if (returnData.returnType === "exchange") {
      return "Exchange complete";
    }
    return "Return complete";
  }

  // For open/in progress returns
  if (!isRejected) {
    return "Return in progress";
  }

  return "Return rejected";
}
